'use client';

import {
  useRoute,
  useNavigation,
  useFocusEffect,
} from '@react-navigation/native';
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { QrCodeSvg } from 'react-native-qr-svg';
import { RFValue } from 'react-native-responsive-fontsize';
import { useTheme } from 'styled-components/native';
import BackgroundTimer from 'react-native-background-timer';
import RNScreenshotPrevent from 'react-native-screenshot-prevent';
import {
  ActivityIndicator,
  Dimensions,
  Linking,
  Modal,
  Platform,
  TouchableWithoutFeedback,
  useColorScheme,
} from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  Easing,
} from 'react-native-reanimated';

import {
  GoBackButton,
  Container,
  Content,
  Wrapper,
  QRCodeContainer,
  QRCodeExpiryDate,
  UserCodeContainer,
  UserCodeTitle,
  UserCode,
  CountdownTimer,
  Overlay,
  LockIcon,
  RefreshButton,
  RefreshButtonText,
  CouponInfoRow,
  CouponInfoTitle,
  CouponInfoValue,
  DashedDivider,
  CodeTypeSwitcher,
  CodeTypeButton,
  CodeTypePrevious,
  CodeType,
  CodeTypeNext,
  QRCodeTitle,
  QRCodeAutCodeContainer,
  QRCodeAutCod,
  AutContainer,
  TitleCodeTypeButton,
  FooterActionsContainer,
  ActionButton,
  ActionButtonIcon,
  ActionButtonTitle,
  CountdownContainer,
  SupportOverlay,
  SupportModalCard,
  SupportModalContent,
  SupportTitle,
  SupportDescription,
  SupportActions,
  SupportConfirmButton,
  SupportConfirmText,
  SupportInput,
  SupportInputCounter,
  SupportInputLabel,
  CouponInformationContainer,
  UserCodeWrapper,
  UserCodeTitleContainer,
  CarouselViewport,
  CarouselItemPressable,
  QRCodeFrame,
  QR_VIEWPORT,
  QR_SVG,
  QRCodeHeaderTitle,
  QRCodeHeader,
  TickerBottom,
  TicketHeader,
  GoBackImage,
  TickerPadding,
  Footer,
  QRCodeHeaderDescription,
} from './styles';

import type { VoucherDTO } from '../../dtos/VoucherDTO';

import {api} from '../../services/api';
import Carousel, {ICarouselInstance} from 'react-native-reanimated-carousel';
import {useAuth} from '../../context/auth';

import ticketHeader from '../../assets/ticket-top.webp';
import ticketFooter from '../../assets/ticket-bottom.webp';

import ticketHeaderDark from '../../assets/ticket-top-dark.webp';
import ticketFooterDark from '../../assets/ticket-bottom-dark.webp';

import { SafeAreaView } from 'react-native-safe-area-context';

import goBackIcon from '../../assets/arrow-back.webp';
import goBackIconLight from '../../assets/arrow-back-light.webp';

import { Ads } from '../../components/shared/Ads';

interface Params {
  data: VoucherDTO;
}

export function Coupon() {
  const route = useRoute();
  const navigation = useNavigation();
  const { data } = route.params as Params;
  const { user } = useAuth();

  const theme = useTheme();

  const colorScheme = useColorScheme();

  const [renderedAt, setRenderedAt] = useState<string>('');
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [codAutoriza, setCodAutoriza] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingCode, setIsLoadingCode] = useState(true);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [problemDescription, setProblemDescription] = useState('');

  const isMountedRef = useRef(true);
  const intervalRef = useRef<number | null>(null);

  const serverTimeRef = useRef<number>(0);
  const expiresAtRef = useRef<number | null>(null);

  const isExpired = timeLeft <= 0;

  const translateY = useSharedValue(Dimensions.get('window').height);
  const opacity = useSharedValue(0);

  function startAnimation() {
    requestAnimationFrame(() => {
      translateY.value = withTiming(0, {
        duration: 450,
        easing: Easing.out(Easing.cubic),
      });
      opacity.value = withTiming(1, { duration: 350 });
    });
  }

  const contentAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
    opacity: opacity.value,
  }));

  const clearTick = useCallback(() => {
    if (intervalRef.current !== null) {
      BackgroundTimer.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // 🚀 Timer incrementa o "serverTimeRef" ao invés de Date.now()
  const startTick = useCallback(() => {
    clearTick();

    intervalRef.current = BackgroundTimer.setInterval(() => {
      if (!expiresAtRef.current) {
        return;
      }

      // Incrementa 1 segundo manualmente
      serverTimeRef.current += 1000;

      const diff = Math.ceil(
        (expiresAtRef.current - serverTimeRef.current) / 1000,
      );

      setTimeLeft(diff > 0 ? diff : 0);

      if (diff <= 0) {
        clearTick();
      }
    }, 1000) as unknown as number;
  }, [clearTick]);

  const backgroundGradient = useMemo(
    () =>
      data.categoria === 'ouro'
        ? theme.colors.gold_gradient
        : theme.colors.silver_gradient,
    [data.categoria, theme.colors.gold_gradient, theme.colors.silver_gradient],
  );

  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }, []);

  // 🚀 Agora o timer é 100% baseado na hora do servidor
  const primeTimerWith = useCallback(
    (serverNowISO: string, validadeSegundos: number) => {
      setRenderedAt(serverNowISO);

      const serverNowMs = new Date(serverNowISO).getTime();

      serverTimeRef.current = serverNowMs;
      expiresAtRef.current = serverNowMs + validadeSegundos * 1000;

      const initialDiff = Math.ceil(
        (expiresAtRef.current - serverTimeRef.current) / 1000,
      );

      setTimeLeft(initialDiff > 0 ? initialDiff : 0);

      startTick();
    },
    [startTick],
  );

  const fetchAuthorizationCode = useCallback(async () => {
    setIsLoadingCode(true);
    try {
      const response = await api.get(`/codigo_abastece?cupomId=${data.id}`);
      if (isMountedRef.current) {
        setCodAutoriza(String(response.data ?? ''));
      }
    } catch {
      if (isMountedRef.current) {
        setCodAutoriza(null);
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoadingCode(false);
      }
    }
  }, [data.id]);

  const fetchServerTime = useCallback(async () => {
    try {
      const response = await api.get('/hora');
      const validade = response.data?.validade_segundos;
      const horaAtual = response.data?.hora_atual;

      if (!validade || !horaAtual) {
        const fallbackISO = new Date().toISOString();
        primeTimerWith(fallbackISO, 60);
        return;
      }

      primeTimerWith(horaAtual, validade);
    } catch {
      const fallbackISO = new Date().toISOString();
      primeTimerWith(fallbackISO, 60);
    }
  }, [primeTimerWith]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearTick();
    };
  }, [clearTick]);

  useFocusEffect(
    useCallback(() => {
      try {
        RNScreenshotPrevent.enabled(true);
        if (Platform.OS === 'ios') {
          RNScreenshotPrevent.enableSecureView();
        }
      } catch { }
      return () => {
        try {
          RNScreenshotPrevent.enabled(false);
          if (Platform.OS === 'ios') {
            RNScreenshotPrevent.disableSecureView();
          }
        } catch { }
      };
    }, []),
  );

  useEffect(() => {
    fetchServerTime();
    fetchAuthorizationCode();
    startAnimation();

    // Fallback: garante visibilidade caso a animação não dispare no Android
    const fallback = setTimeout(() => {
      translateY.value = 0;
      opacity.value = 1;
    }, 700);

    return () => clearTimeout(fallback);
  }, [fetchServerTime, fetchAuthorizationCode]);

  const handleCouponsStations = useCallback(() => {
    navigation.navigate('CouponStationsNovo', {
      region: data.region,
      voucherId: data.id,
      tipo: data.tipo,
    });
  }, [navigation, data.region, data.id, data.tipo]);

  const handleEmergencySupportPress = useCallback(() => {
    setShowSupportModal(true);
  }, []);

  const handleCloseSupportModal = useCallback(() => {
    setShowSupportModal(false);
    setProblemDescription('');
  }, []);

  const handleEmergencySupport = useCallback(async () => {
    const phone = '5561998202004';

    const formatLitersForSupport = (liters?: string) => {
      if (!liters) {
        return 'Não informado';
      }
      const trimmed = String(liters).trim();
      if (!trimmed) {
        return 'Não informado';
      }
      return /l/i.test(trimmed) ? trimmed : `${trimmed} L`;
    };

    const regionLabel = [data?.cidade, data?.localidade ?? data?.region]
      .filter(Boolean)
      .join(' - ');

    const fuelLabel = data?.name
      ? data.name.charAt(0).toUpperCase() + data.name.slice(1)
      : 'Não informado';

    const DEFAULT_LABEL = 'Não informado';

    const VALOR_TOTAL_NO_CENTS = data?.valorTotalJurosCents;
    const VALOR_JUROS_LABEL =
      VALOR_TOTAL_NO_CENTS != null
        ? Number(VALOR_TOTAL_NO_CENTS).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        : DEFAULT_LABEL;

    const PAYMENT_METHOD_LABEL = data?.paymentMethodo || DEFAULT_LABEL;

    const NOME_LABEL = `Nome: ${user?.name ?? DEFAULT_LABEL}`;
    const CPF_LABEL = `CPF: ${user?.cpf ?? DEFAULT_LABEL}`;
    const EMAIL_LABEL = `E-mail: ${user?.email ?? DEFAULT_LABEL}`;
    const LITRAGEM_LABEL = data?.tipo === 'C' ? `Litragem: ${formatLitersForSupport(data?.liters)}` : null;
    const REGIAO_LABEL = `Região: ${regionLabel || DEFAULT_LABEL}`;
    const PRODUTO_LABEL = `Produto: ${fuelLabel}`;
    const VALOR_LABEL = `Valor total com juros: ${VALOR_JUROS_LABEL}`;
    const METODO_LABEL = `Método de pagamento: ${PAYMENT_METHOD_LABEL}`;
    const PROBLEMA_LABEL = `Problema: ${problemDescription.trim() || DEFAULT_LABEL}`;

    const message = encodeURIComponent(
      [
        'Olá, preciso de ajuda urgente.',
        '',
        NOME_LABEL,
        CPF_LABEL,
        EMAIL_LABEL,
        LITRAGEM_LABEL,
        REGIAO_LABEL,
        PRODUTO_LABEL,
        VALOR_LABEL,
        METODO_LABEL,
        PROBLEMA_LABEL,
      ]
        .filter(Boolean)
        .join('\n'),
    );
    const url = `https://wa.me/${phone}?text=${message}`;
    setShowSupportModal(false);
    setProblemDescription('');
    Linking.openURL(url).catch(() => { });
  }, [data, user?.cpf, user?.email, user?.name, problemDescription]);

  const handleRefreshCoupon = useCallback(async () => {
    if (isRefreshing) {
      return;
    }

    setIsRefreshing(true);
    try {
      clearTick();
      await fetchServerTime();
      await fetchAuthorizationCode();
    } finally {
      if (isMountedRef.current) {
        setIsRefreshing(false);
      }
    }
  }, [clearTick, fetchServerTime, fetchAuthorizationCode, isRefreshing]);

  const carouselRef = useRef<ICarouselInstance>(null);
  const currentIndexRef = useRef(0);
  const [activeType, setActiveType] = useState<'QR' | 'aut'>('QR');

  const qrValue = useMemo(
    () =>
      `https://barataocombustiveis.com.br/abastece?cupom=${data.id
      }&dataGeracao=${encodeURIComponent(renderedAt)}`,
    [data.id, renderedAt],
  );

  const codeTypeData = useMemo(
    () => [
      {
        type: 'QR',
        render: () => (
          <QRCodeFrame>
            <QrCodeSvg
              value={qrValue}
              frameSize={QR_SVG}
              backgroundColor="#fff"
              dotColor="black"
            />
          </QRCodeFrame>
        ),
      },
      {
        type: 'aut',
        render: () => (
          <AutContainer>
            <QRCodeTitle>Código PDV</QRCodeTitle>
            <QRCodeAutCodeContainer>
              {isLoadingCode ? (
                <ActivityIndicator size="small" />
              ) : (
                <QRCodeAutCod>{codAutoriza ?? '••••'}</QRCodeAutCod>
              )}
            </QRCodeAutCodeContainer>
          </AutContainer>
        ),
      },
    ],
    [qrValue, isLoadingCode, codAutoriza],
  );

  const handleCarouselSnapToItem = useCallback(
    (index: number) => {
      const normalizedIndex =
        ((index % codeTypeData.length) + codeTypeData.length) %
        codeTypeData.length;

      currentIndexRef.current = normalizedIndex;
      setActiveType(codeTypeData[normalizedIndex].type as 'QR' | 'aut');
    },
    [codeTypeData],
  );

  const handleChangeCodeType = useCallback((dir: 'prev' | 'next') => {
    const total = 2;
    const curr = currentIndexRef.current;
    const next =
      dir === 'next' ? (curr + 1) % total : (curr - 1 + total) % total;

    setActiveType(next === 0 ? 'QR' : 'aut');
    currentIndexRef.current = next;

    if (dir === 'next') {
      carouselRef.current?.next();
    } else {
      carouselRef.current?.prev();
    }
  }, []);

  const backgroundToneSchemeTop =
    colorScheme === 'dark' && Platform.OS !== 'web' ? ticketHeaderDark : ticketHeader;
  const backgroundToneSchemeBottom =
    colorScheme === 'dark' && Platform.OS !== 'web' ? ticketFooterDark : ticketFooter;

  const arrowIconToneScheme =
    colorScheme === 'dark' && Platform.OS !== 'web' ? goBackIconLight : goBackIcon;

  return (
    <>
      <Modal
        visible={isExpired}
        transparent
        animationType="fade"
        statusBarTranslucent
        onRequestClose={() => { }}>
        <Overlay>
          <LockIcon />
          <QRCodeExpiryDate>
            Seu QR Code expirou, toque no botão para renovar.
          </QRCodeExpiryDate>
          <RefreshButton disabled={isRefreshing} onPress={handleRefreshCoupon}>
            <RefreshButtonText>
              {isRefreshing ? 'Renovando…' : 'Renovar cupom'}
            </RefreshButtonText>
          </RefreshButton>
        </Overlay>
      </Modal>

      <Modal
        visible={showSupportModal}
        transparent
        animationType="fade"
        statusBarTranslucent
        onRequestClose={handleCloseSupportModal}>
        <TouchableWithoutFeedback onPress={handleCloseSupportModal}>
          <SupportOverlay>
            <TouchableWithoutFeedback>
              <SupportModalCard>
                <SupportModalContent>
                  <SupportTitle>Suporte emergencial</SupportTitle>
                  <SupportDescription>
                    Está com algum problema para abastecer neste momento?
                  </SupportDescription>
                  <SupportDescription style={{ marginTop: RFValue(10) }}>
                    Ao continuar, você será direcionado para nosso atendimento
                    prioritário no WhatsApp. Algumas informações da sua compra serão
                    enviadas automaticamente para agilizar o suporte.
                  </SupportDescription>
                  <SupportDescription style={{ marginTop: RFValue(10) }}>
                    Use esta opção apenas se estiver no posto e precisar de ajuda
                    imediata.
                  </SupportDescription>

                  <SupportInputLabel>Descreva o problema</SupportInputLabel>
                  <SupportInput
                    placeholder="Ex: QR Code não apareceu"
                    placeholderTextColor={theme.colors.placeholder}
                    value={problemDescription}
                    onChangeText={setProblemDescription}
                    multiline
                    maxLength={100}
                  />
                  <SupportInputCounter>{problemDescription.length}/100</SupportInputCounter>

                  <SupportActions>
                    <SupportConfirmButton onPress={handleEmergencySupport} disabled={!problemDescription.trim()} style={{ opacity: problemDescription.trim() ? 1 : 0.4 }}>
                      <SupportConfirmText>
                        Continuar com atendimento
                      </SupportConfirmText>
                    </SupportConfirmButton>
                  </SupportActions>
                </SupportModalContent>
              </SupportModalCard>
            </TouchableWithoutFeedback>
          </SupportOverlay>
        </TouchableWithoutFeedback>
      </Modal>

      <Container colors={backgroundGradient} style={{ justifyContent: 'center' }}>
        <SafeAreaView edges={['top']}>
          <Content as={Animated.View} style={contentAnimatedStyle}>
            <Wrapper>
              <QRCodeContainer pointerEvents={isExpired ? 'none' : 'auto'}>
                <TicketHeader
                  source={backgroundToneSchemeTop}
                  resizeMode="stretch">
                  <GoBackButton onPress={() => navigation.goBack()}>
                    <GoBackImage source={arrowIconToneScheme} />
                  </GoBackButton>
                  <TickerPadding>
                    <QRCodeHeader>
                      <QRCodeHeaderTitle>Aponte para leitor</QRCodeHeaderTitle>
                      <QRCodeHeaderDescription>
                        Seu cupom continua válido; renove apenas o QR Code
                        quando o temporizador zerar.
                      </QRCodeHeaderDescription>
                    </QRCodeHeader>

                    <CouponInformationContainer>
                      <CouponInfoRow>
                        <CouponInfoTitle>Produto:</CouponInfoTitle>
                        <CouponInfoValue>
                          {data.name
                            ? data.name.charAt(0).toUpperCase() +
                            data.name.slice(1)
                            : 'Não informado'}
                        </CouponInfoValue>
                      </CouponInfoRow>

                      <DashedDivider colorScheme={colorScheme} />

                      <CouponInfoRow style={{ marginBottom: -RFValue(4) }}>
                        <CouponInfoTitle>Tempo restante:</CouponInfoTitle>
                        <CouponInfoValue>
                          <CountdownContainer>
                            <CountdownTimer>
                              {formatTime(timeLeft)}
                            </CountdownTimer>
                          </CountdownContainer>
                        </CouponInfoValue>
                      </CouponInfoRow>

                      <DashedDivider colorScheme={colorScheme} />

                      {data.tipo === 'C' && (
                        <CouponInfoRow>
                          <CouponInfoTitle>Litragem:</CouponInfoTitle>
                          <CouponInfoValue>
                            {data.liters} Litros
                          </CouponInfoValue>
                        </CouponInfoRow>
                      )}
                    </CouponInformationContainer>
                  </TickerPadding>
                </TicketHeader>

                <TickerBottom
                  source={backgroundToneSchemeBottom}
                  resizeMode="stretch">
                  <TickerPadding>
                    <CarouselViewport>
                      <Carousel
                        ref={carouselRef}
                        data={codeTypeData}
                        loop
                        width={QR_VIEWPORT}
                        height={QR_VIEWPORT}
                        renderItem={({ item }) => (
                          <CarouselItemPressable
                            onPress={() => handleChangeCodeType('next')}
                            android_disableSound>
                            {item.render()}
                          </CarouselItemPressable>
                        )}
                        onSnapToItem={handleCarouselSnapToItem}
                      />
                    </CarouselViewport>

                    <CodeTypeSwitcher>
                      <CodeTypeButton
                        onPress={() => handleChangeCodeType('prev')}>
                        <CodeTypePrevious name="chevron-left" />
                      </CodeTypeButton>

                      <TitleCodeTypeButton
                        activeOpacity={1}
                        onPress={() => handleChangeCodeType('next')}>
                        <CodeType>
                          {activeType === 'QR' ? 'Código PDV' : 'QR Code'}
                        </CodeType>
                      </TitleCodeTypeButton>

                      <CodeTypeButton
                        onPress={() => handleChangeCodeType('next')}>
                        <CodeTypeNext name="chevron-right" />
                      </CodeTypeButton>
                    </CodeTypeSwitcher>

                    <UserCodeWrapper>
                      <UserCodeTitleContainer>
                        <UserCodeTitle>Código de liberação:</UserCodeTitle>
                      </UserCodeTitleContainer>
                      <UserCodeContainer>
                        <UserCode>4 Primeiros Dígitos Do Seu CPF</UserCode>
                      </UserCodeContainer>
                    </UserCodeWrapper>

                    <FooterActionsContainer>
                      <ActionButton
                        onPress={handleEmergencySupportPress}
                        style={{
                          backgroundColor: theme.colors.newCuponScreenRedLight,
                          borderStyle: 'solid',
                          borderWidth: 1,
                          borderColor: theme.colors.newCouponScreenRed,
                        }}>
                        <ActionButtonIcon
                          name="alert-circle"
                          iconColor={theme.colors.newCouponScreenRed}
                        />
                        <ActionButtonTitle
                          style={{ color: theme.colors.newCouponScreenRed }}>
                          Preciso de{'\n'}Ajuda Urgente
                        </ActionButtonTitle>
                      </ActionButton>
                      <ActionButton
                        onPress={handleCouponsStations}
                        style={{
                          backgroundColor:
                            theme.colors.newCuponScreenGreenLight,
                          borderStyle: 'solid',
                          borderWidth: 1,
                          borderColor: theme.colors.newCouponScreenGreen,
                        }}>
                        <ActionButtonIcon
                          name="map-pin"
                          iconColor={theme.colors.newCouponScreenGreen}
                        />
                        <ActionButtonTitle
                          style={{ color: theme.colors.newCouponScreenGreen }}>
                          Ver {data.tipo === 'C' ? 'Postos' : 'Lojas'}
                          {'\n'}Disponíveis
                        </ActionButtonTitle>
                      </ActionButton>
                    </FooterActionsContainer>
                  </TickerPadding>
                </TickerBottom>
              </QRCodeContainer>
            </Wrapper>
          </Content>
        </SafeAreaView>

        <Footer>
          <Ads />
        </Footer>
      </Container>
    </>
  );
}
